import React, {useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {enqueueSnackbar} from 'notistack'
import i18n from '../../i18n'
import {RootState} from '../../store.ts'
import {CreatedTripbook, HiddenUnit, InactiveCode, PackTraveler, PackTrip, PrintedTripbook} from '../../domain/entities/B2BPackDetail.ts'
import {buildInvitationCodeUpdatePayload, InvitationCodeRecord, InvitationCodeTraveler} from '../../domain/entities/InvitationCode.ts'
import {TripbookFeedback} from '../../domain/entities/TripbookFeedback.ts'
import {TripbookPackage} from '../../domain/entities/Agency.ts'
import {AssignInvitationCodeDialog, AvailableInvitationCode} from '../dialogs/AssignInvitationCodeDialog.tsx'
import {EditInvitationCodeChanges, EditInvitationCodeDialog, EditInvitationCodeMode} from '../dialogs/EditInvitationCodeDialog.tsx'
import {EditTripbookFeedbackDialog} from '../dialogs/EditTripbookFeedbackDialog.tsx'
import {ConfirmDialog} from '../dialogs/ConfirmDialog.tsx'
import {SharedAlbumTravelersDialog} from '../dialogs/SharedAlbumTravelersDialog.tsx'
import {useCancelInvitationCode, useUpdateInvitationCode} from '../../hooks/useInvitationCodes.ts'
import {useTripbookFeedback} from '../../hooks/useTripbookFeedback.ts'
import {useHiddenUnits} from '../../hooks/useHiddenUnits.ts'
import {CodeTableItem, pageOptionsFor, codeGroupKind, groupCodeRows, hideGenericDuplicates, isFreeCodeRow, isSharedAlbum, unifyCodeRows, UnifiedCodeRow, DELIVERY_LABEL_KEY} from './unifyCodeRows.ts'
import {GroupBadge, GroupPill, GroupToggle, MemberCode, TravelerListButton} from './CodeGroupParts.tsx'
import {useExpandedGroups} from '../../hooks/useExpandedGroups.ts'
import {CodeStatusPill, DuplicateTripBadge, LIFECYCLE_EDITABLE} from './CodeStatusPill.tsx'
import {CodeStatusMenu} from './CodeStatusMenu.tsx'
import {TripbookSidePanel} from './TripbookSidePanel.tsx'
import {getFlagUrlFromIso2} from '../../utils/flagUrl.ts'
import {formatDate} from '../../utils/formatDate.ts'
import {formatNumber} from '../../utils/formatNumber.ts'
import {deliverableLink} from '../../utils/redemptionUrl.ts'
import {AuthenticationRepository} from '../../repositories/AuthenticationRepository.ts'
import {TripbookRepository} from '../../repositories/TripbookRepository.ts'
import {DownloadEbookUseCase} from '../../domain/useCases/tripbooks/DownloadEbookUseCase.ts'
import {RegenerateEbookUseCase} from '../../domain/useCases/tripbooks/RegenerateEbookUseCase.ts'
import {userReadableDescription} from '../../utils/UserReadableDescription.ts'
import IconCopy from '../../assets/icons/copy.svg?react'
import IconPlus from '../../assets/icons/plus.svg?react'
import IconEdit from '../../assets/icons/edit.svg?react'
import IconChevronDown from '../../assets/icons/chevron-down.svg?react'
import IconPackage from '../../assets/icons/package.svg?react'
import IconCheck from '../../assets/icons/check.svg?react'
import IconClose from '../../assets/icons/close.svg?react'
import IconPdfDoc from '../../assets/icons/pdf-doc.svg?react'
import IconDownload from '../../assets/icons/download-arrow.svg?react'
import IdeaIcon from '../../assets/icons/idea.svg?react'
import IconTrash from '../../assets/icons/trash.svg?react'

const PAGE_SIZE = 10

const tripbookRepository = new TripbookRepository(new AuthenticationRepository())
const downloadEbookUseCase = new DownloadEbookUseCase(tripbookRepository)
const regenerateEbookUseCase = new RegenerateEbookUseCase(tripbookRepository)

const saveBlob = (blob: Blob, code: string) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${code}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

// Same blob-to-<a> pattern as the legacy "Descargar ebook" menu item in
// TripbooksContainer — the endpoint always returns the PDF as a blob, there's
// no separate public URL to preview it first. A failed request now properly
// rejects (see TripbookRepository's response interceptor) and lands in
// .catch(); this instanceof check is just a defensive last line in case a
// 2xx ever comes back without a real blob body. Resolves false (after telling
// the user) when there's no PDF to hand over, so callers can stop a spinner.
export const downloadEbookPdf = (code: string): Promise<boolean> =>
    downloadEbookUseCase.execute(code).then((result) => {
        const blob = result as unknown
        if (!(blob instanceof Blob)) {
            console.error('downloadEbookPdf: no ebook available for code', code, result)
            enqueueSnackbar(i18n.t('packs.pdfNotAvailable', {ns: 'dashboard'}), {variant: 'error'})
            return false
        }
        saveBlob(blob, code)
        return true
    }).catch((error) => {
        console.error(error)
        enqueueSnackbar(i18n.t('packs.pdfNotAvailable', {ns: 'dashboard'}), {variant: 'error'})
        return false
    })

// POST /invitation-codes/{code}/ebook — re-renders the gift PDF. If the backend
// answers with the PDF itself it's downloaded straight away; otherwise the user
// is told it's ready and can grab it with "Descargar PDF".
export const regenerateEbookPdf = (code: string): Promise<boolean> =>
    regenerateEbookUseCase.execute(code).then((result) => {
        if (result instanceof Blob && result.type === 'application/pdf') {
            saveBlob(result, code)
        }
        enqueueSnackbar(i18n.t('packs.pdfRegenerated', {ns: 'dashboard'}), {variant: 'success'})
        return true
    }).catch((error) => {
        console.error(error)
        enqueueSnackbar(i18n.t('packs.pdfRegenerateError', {ns: 'dashboard'}), {variant: 'error'})
        return false
    })

// "Sin asignar" groups codes with no trip yet and codes marked delivered but
// not yet redeemed — the only two lifecycles an agency can toggle by hand
// (see LIFECYCLE_EDITABLE in CodeStatusPill.tsx).
const isUnassignedRow = (row: UnifiedCodeRow) => row.lifecycle === 'NOT_ASSIGNED' || row.lifecycle === 'DELIVERED'

// Outlined pill — pale tint + colored border/text, no dot — distinct from
// CodeStatusPill's filled dot-pill so the shipment stage never reads as
// another code-lifecycle state.
const DELIVERY_STYLE: Record<string, string> = {
    IN_PRODUCTION: 'bg-[#FDF7E6] border-[#E8A200] text-[#7A5400]',
    IN_SHIPPING: 'bg-[#FDEFF1] border-[#E08095] text-[#993349]',
    DELIVERED: 'bg-[#EAF8FA] border-[#04A3B3] text-[#046069]',
    // No shipment incident in the design's map — keep the app's danger tint.
    INCIDENT: 'bg-danger/15 border-danger text-danger',
}

export function usePagedSlice<T>(items: T[]) {
    const [page, setPage] = useState(0)
    const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
    const currentPage = Math.min(page, pageCount - 1)
    const pageItems = items.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE)
    return {pageItems, page: currentPage, setPage, pageCount}
}

interface PackActivityTablesProps {
    agencyId: string
    packContext?: string
    printed: PrintedTripbook[]
    created: CreatedTripbook[]
    inactive: InactiveCode[]
    onCodeCreated: () => void
    invitationCodesById: Map<string, InvitationCodeRecord>
    // Ids of reusable agency-wide codes (unlimitedRedemption) — a free one and/or
    // a paid one — belong in the top banner instead, never as pending rows here.
    excludeCodeIds?: Set<string>
    // Contracted units across all packs — undefined for pay-per-use agencies
    // (AgencyActivityCard), which have no pack/unit concept.
    totalUnits?: number
    // Decides which redemption host the assigned code's link is built on.
    primaryMarket?: string
    // Packs (units + pagination) and the agency's own pages, for the assign
    // dialog's "Paginación del TripBook" options. Absent for pay-per-use.
    packages?: TripbookPackage[]
    agencyPages?: number
}

// Three tables — impresos, creados, sin asignar (unassigned + delivered) —
// instead of one flat list, so the agency can scan each stage on its own.
// Shared by PacksTripbooksSection (all of an agency's packs pooled together,
// no per-pack grouping) and AgencyActivityCard (agency-wide, pay-per-use).
export const PackActivityTables: React.FC<PackActivityTablesProps> = ({
    agencyId, packContext, printed, created, inactive, onCodeCreated, invitationCodesById, excludeCodeIds, totalUnits, primaryMarket, packages, agencyPages = 60,
}) => {
    const {t} = useTranslation('dashboard')
    const visibleInactive = useMemo(
        () => excludeCodeIds?.size ? inactive.filter((row) => !excludeCodeIds.has(row.codeId)) : inactive,
        [inactive, excludeCodeIds]
    )
    // Trips support hid by hand (CreatedTripbook.hidden) leave the main tables
    // and every count before anything else reads the rows; they get their own
    // collapsed section below so staff can bring them back.
    const shownCreated = useMemo(() => created.filter((c) => !c.hidden), [created])
    const hiddenCreated = useMemo(() => created.filter((c) => !!c.hidden), [created])
    // Generic-link duplicates are dropped here, before anything else reads the
    // rows; unique-code ones stay, flagged in duplicateKeys (see hideGenericDuplicates).
    const {rows, duplicateKeys} = useMemo(
        () => hideGenericDuplicates(unifyCodeRows(printed, shownCreated, visibleInactive, invitationCodesById, excludeCodeIds), excludeCodeIds),
        [printed, shownCreated, visibleInactive, invitationCodesById, excludeCodeIds]
    )
    const hiddenByTripId = useMemo(
        () => new Map(hiddenCreated.map((c) => [c.tripId, c.hidden as HiddenUnit])),
        [hiddenCreated]
    )
    const hiddenRows = useMemo(
        () => unifyCodeRows([], hiddenCreated, [], invitationCodesById, excludeCodeIds),
        [hiddenCreated, invitationCodesById, excludeCodeIds]
    )
    const hiddenPaging = usePagedSlice(hiddenRows)

    const printedRows = useMemo(() => rows.filter((r) => r.lifecycle === 'PRINTED'), [rows])
    // Books, not rows: a trip printed twice is one row but two books.
    const printedBooks = useMemo(() => printedRows.reduce((acc, r) => acc + r.printedCopies, 0), [printedRows])
    const createdRows = useMemo(() => rows.filter((r) => r.lifecycle === 'TRIP_CREATED'), [rows])
    const unassignedRows = useMemo(() => rows.filter(isUnassignedRow), [rows])

    // "TripBook creado" and "Códigos prerellenados" page over items: a "Grupo -
    // Álbum individual" is one collapsible item (see groupCodeRows). Printed
    // codes stay one per row and just carry a group badge, as in the design.
    const createdItems = useMemo(() => groupCodeRows(createdRows, invitationCodesById), [createdRows, invitationCodesById])
    const unassignedItems = useMemo(() => groupCodeRows(unassignedRows, invitationCodesById), [unassignedRows, invitationCodesById])

    const printedPaging = usePagedSlice(printedRows)
    const createdPaging = usePagedSlice(createdItems)
    const unassignedPaging = usePagedSlice(unassignedItems)
    const expandedGroups = useExpandedGroups()
    // Same rule as the agency view: assign a code that's still free before
    // minting a new one, so the button never inflates a pack's code count.
    const freeRows = useMemo(() => rows.filter(isFreeCodeRow), [rows])

    const [copied, setCopied] = useState<string | null>(null)
    const copyTimeout = useRef<ReturnType<typeof setTimeout>>()
    const assignDialog = useRef<React.ElementRef<typeof AssignInvitationCodeDialog>>(null)
    // The whole free pool, in order: one TripBook consumes the first code, "un
    // TripBook diferente para cada viajero" the first N (see AssignInvitationCodeDialog).
    const pageOptions = useMemo(
        () => pageOptionsFor(packages ?? [], agencyPages, rows, invitationCodesById, excludeCodeIds),
        [packages, agencyPages, rows, invitationCodesById, excludeCodeIds]
    )
    const availableCodes = useMemo<AvailableInvitationCode[]>(() => freeRows.flatMap((row) => {
        const record = row.codeId ? invitationCodesById.get(row.codeId) : undefined
        return record ? [{record, url: row.deliverableUrl ?? null}] : []
    }), [freeRows, invitationCodesById])
    const editDialog = useRef<React.ElementRef<typeof EditInvitationCodeDialog>>(null)
    const feedbackDialog = useRef<React.ElementRef<typeof EditTripbookFeedbackDialog>>(null)
    const [editingCodeId, setEditingCodeId] = useState<string | null>(null)
    const [editingMode, setEditingMode] = useState<EditInvitationCodeMode>('traveler')
    const [feedbackRow, setFeedbackRow] = useState<UnifiedCodeRow | null>(null)
    // Stores only the key, not the row snapshot: the side panel must reflect
    // the freshly refetched row after a status change, not the one captured
    // when it was opened (otherwise it visibly lags behind the table).
    const [openRowKey, setOpenRowKey] = useState<string | null>(null)
    const openRow = openRowKey ? rows.find((r) => r.key === openRowKey) ?? null : null
    const [statusMenuKey, setStatusMenuKey] = useState<string | null>(null)
    const [feedbackPopoverKey, setFeedbackPopoverKey] = useState<string | null>(null)
    const cancelDialog = useRef<React.ElementRef<typeof ConfirmDialog>>(null)
    const [cancelTarget, setCancelTarget] = useState<UnifiedCodeRow | null>(null)

    const {updateInvitationCode, isUpdatingInvitationCode} = useUpdateInvitationCode(agencyId)
    const {setTripbookFeedback, isSettingTripbookFeedback, deleteTripbookFeedback, isDeletingTripbookFeedback} = useTripbookFeedback(agencyId)
    const {cancelInvitationCode, isCancelingInvitationCode} = useCancelInvitationCode(agencyId)
    const {hideUnit, unhideUnit, isChangingHidden} = useHiddenUnits(agencyId)
    const hideDialog = useRef<React.ElementRef<typeof ConfirmDialog>>(null)
    const [hideTarget, setHideTarget] = useState<UnifiedCodeRow | null>(null)
    const [hideReason, setHideReason] = useState('')

    const user = useSelector((state: RootState) => state.auth.user)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isAdmin = (user as any)?.role === 'admin'

    const editingRecord = editingCodeId ? invitationCodesById.get(editingCodeId) ?? null : null
    const recordOf = (row: UnifiedCodeRow) => (row.codeId ? invitationCodesById.get(row.codeId) : undefined)

    const travelersDialog = useRef<React.ElementRef<typeof SharedAlbumTravelersDialog>>(null)
    const [travelersCodeId, setTravelersCodeId] = useState<string | null>(null)
    const travelersRecord = travelersCodeId ? invitationCodesById.get(travelersCodeId) ?? null : null

    const openTravelerList = (codeId: string) => {
        setTravelersCodeId(codeId)
        travelersDialog.current?.open()
    }

    const handleSaveTravelers = (travelers: InvitationCodeTraveler[]) => {
        if (!travelersRecord) return Promise.resolve()
        return updateInvitationCode(buildInvitationCodeUpdatePayload(travelersRecord, {travelers}))
    }

    const copyLink = (key: string, url: string) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(key)
            clearTimeout(copyTimeout.current)
            copyTimeout.current = setTimeout(() => setCopied(null), 1400)
        })
    }

    const openAssignDialog = () => {
        assignDialog.current?.open()
    }

    const openEdit = (codeId: string, mode: EditInvitationCodeMode) => {
        setEditingCodeId(codeId)
        setEditingMode(mode)
        editDialog.current?.open()
    }

    const handleStatusChange = (row: UnifiedCodeRow, isDelivered: boolean) => {
        if (!row.codeId) return
        const record = invitationCodesById.get(row.codeId)
        if (!record) return
        updateInvitationCode(buildInvitationCodeUpdatePayload(record, {isDelivered})).catch(userReadableDescription)
    }

    const handleSaveEdit = (changes: EditInvitationCodeChanges) => {
        if (!editingRecord) return
        updateInvitationCode(buildInvitationCodeUpdatePayload(editingRecord, changes))
            .then(() => editDialog.current?.close())
            .catch(console.error)
    }

    const openFeedbackEditor = (row: UnifiedCodeRow) => {
        setFeedbackPopoverKey(null)
        setFeedbackRow(row)
        feedbackDialog.current?.open()
    }

    const handleSaveFeedback = (feedback: TripbookFeedback) => {
        if (!feedbackRow?.tripId || !feedbackRow.tripbookId) return
        setTripbookFeedback({tripId: feedbackRow.tripId, tripbookId: feedbackRow.tripbookId, payload: feedback})
            .then(() => feedbackDialog.current?.close())
            .catch(console.error)
    }

    const handleDeleteFeedback = () => {
        if (!feedbackRow?.tripId || !feedbackRow.tripbookId) return
        deleteTripbookFeedback({tripId: feedbackRow.tripId, tripbookId: feedbackRow.tripbookId})
            .then(() => feedbackDialog.current?.close())
            .catch(console.error)
    }

    // This table is always the staff/admin view (never the agency's own login —
    // see AgencySelfServiceCodeSections for that one), so cancel spans every
    // stage up to (not including) printed: "pendiente de entrega" through
    // "TripBook creado". Only unique codes ("Solo aplicable para códigos
    // únicos") — a generic link is one document shared by every redemption.
    const canCancelRow = (row: UnifiedCodeRow) =>
        !!row.codeId && !excludeCodeIds?.has(row.codeId) && row.lifecycle !== 'PRINTED'

    const askCancel = (row: UnifiedCodeRow) => {
        setCancelTarget(row)
        cancelDialog.current?.open()
    }

    // Same fire-and-forget pattern as AgencyUsersSection's confirmDelete:
    // ConfirmDialog closes itself as soon as onConfirm returns truthy, so the
    // request just runs in the background after that.
    const confirmCancel = () => {
        if (!cancelTarget?.codeId) return false
        cancelInvitationCode(cancelTarget.codeId).catch(userReadableDescription)
        setCancelTarget(null)
        return true
    }

    // Support hides by hand what the automatic duplicate rule misses — only
    // trips not printed yet (a printed book is real and always counts), and
    // only off a generic link: a unique code is cancelled instead (canCancelRow).
    const canHideRow = (row: UnifiedCodeRow) =>
        isAdmin && row.lifecycle === 'TRIP_CREATED' && !!row.tripId && (!row.codeId || !!excludeCodeIds?.has(row.codeId))

    const askHide = (row: UnifiedCodeRow) => {
        setHideTarget(row)
        setHideReason('')
        hideDialog.current?.open()
    }

    const confirmHide = () => {
        if (!hideTarget?.tripId) return false
        hideUnit({tripId: hideTarget.tripId, reason: hideReason.trim() || null}).catch(userReadableDescription)
        setHideTarget(null)
        return true
    }

    const hideButton = (row: UnifiedCodeRow) => (
        <button type="button" title={t('packs.hideTripHelp')} disabled={isChangingHidden}
                onClick={(e) => {e.stopPropagation(); askHide(row)}}
                className="h-6 px-2 flex-none inline-flex items-center rounded-full text-[12px] text-neutralBlack-500 hover:bg-neutralBlack-100 hover:text-neutralBlack-900 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
            {t('packs.hideTrip')}
        </button>
    )

    const cancelButton = (row: UnifiedCodeRow) => (
        <button type="button" title={t('packs.cancelCode')} disabled={isCancelingInvitationCode}
                onClick={(e) => {e.stopPropagation(); askCancel(row)}}
                className="w-6 h-6 flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-danger/10 hover:text-danger disabled:opacity-40 disabled:cursor-not-allowed">
            <IconTrash className="size-3"/>
        </button>
    )

    // --- Cell fragments shared across the printed/created/unassigned tables ---

    // Codes that belong to a group album carry its badge (design: "TripBooks
    // impresos"); inside an expanded group the member code is indented instead.
    const codeCell = (row: UnifiedCodeRow, isMember = false) => {
        if (isMember) return <Td><MemberCode code={row.code}/></Td>
        const kind = codeGroupKind(recordOf(row))
        return (
            <Td>
                <span className="inline-flex items-center gap-[9px] font-medium text-neutralBlack-900 tracking-wide whitespace-nowrap">
                    {row.code ?? <Dash/>}
                    {kind && <GroupBadge kind={kind}/>}
                </span>
            </Td>
        )
    }

    const deliverableCell = (row: UnifiedCodeRow) => {
        const url = deliverableLink(row.deliverableUrl, row.code, primaryMarket)
        return (
            <Td>
                {url ? (
                    <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <a href={url} target="_blank" rel="noreferrer" className="text-[13px] whitespace-nowrap">{t('packs.viewLink')}</a>
                        <CopyButton active={copied === `deliverable-${row.key}`} onClick={() => copyLink(`deliverable-${row.key}`, url)}/>
                    </span>
                ) : <Dash/>}
            </Td>
        )
    }

    const statusCell = (row: UnifiedCodeRow, hasRecord: boolean) => {
        const canEditStatus = LIFECYCLE_EDITABLE.includes(row.lifecycle) && hasRecord
        return (
            <Td>
                <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                    <CodeStatusPill
                        lifecycle={row.lifecycle}
                        onClick={canEditStatus ? () => setStatusMenuKey((k) => (k === row.key ? null : row.key)) : undefined}
                    />
                    {statusMenuKey === row.key && canEditStatus && (
                        <CodeStatusMenu
                            delivered={row.lifecycle === 'DELIVERED'}
                            disabled={isUpdatingInvitationCode}
                            onChange={(delivered) => handleStatusChange(row, delivered)}
                            onClose={() => setStatusMenuKey(null)}
                        />
                    )}
                </span>
                {/* A trip printed more than once (several orders) — one row, several books. */}
                {row.printedCopies > 1 && <span className="block mt-1 text-[12px] text-neutralBlack-500">{t('packs.copiesCount', {count: row.printedCopies})}</span>}
                {duplicateKeys.has(row.key) && <span className="block mt-1"><DuplicateTripBadge/></span>}
            </Td>
        )
    }

    // "Códigos prerellenados" toggles delivery inline instead of through the
    // pill-opens-a-menu pattern: a plain "Sin entregar" label with a
    // "Marcar entregado" button, or an "Entregado" pill with an undo button.
    const unassignedStatusCell = (row: UnifiedCodeRow, hasRecord: boolean) => (
        <Td>
            <span onClick={(e) => e.stopPropagation()}>
                {row.lifecycle === 'DELIVERED' ? (
                    <span className="inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D8F3F6] px-2.5 py-1 text-xs text-neutralBlack-900 whitespace-nowrap">
                            <span className="w-[7px] h-[7px] rounded-full flex-none bg-[#04A3B3]"/>
                            {t('packs.statusDelivered')}
                        </span>
                        <button type="button" title={t('packs.markAsNotDelivered')} disabled={!hasRecord || isUpdatingInvitationCode}
                                onClick={() => handleStatusChange(row, false)}
                                className="w-[22px] h-[22px] flex-none flex items-center justify-center rounded-full text-neutralBlack-300 hover:bg-neutralBlack-100 hover:text-neutralBlack-900 disabled:opacity-40 disabled:cursor-not-allowed">
                            <IconClose className="size-2"/>
                        </button>
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-2.5">
                        <span className="text-[12.5px] text-neutralBlack-400 whitespace-nowrap">{t('packs.statusNotDelivered')}</span>
                        <button type="button" disabled={!hasRecord || isUpdatingInvitationCode}
                                onClick={() => handleStatusChange(row, true)}
                                className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full border border-neutralBlack-300 bg-white text-xs font-medium text-neutralBlack-900 hover:bg-neutralBlack-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                            <IconCheck className="size-2.5"/>
                            {t('packs.markAsDelivered')}
                        </button>
                    </span>
                )}
            </span>
            {duplicateKeys.has(row.key) && <span className="block mt-1"><DuplicateTripBadge/></span>}
        </Td>
    )

    const travelerCell = (row: UnifiedCodeRow, hasRecord: boolean) => {
        const record = recordOf(row)
        if (record && isSharedAlbum(record)) {
            return <Td><TravelerListButton record={record} onOpen={() => openTravelerList(record.id)}/></Td>
        }
        return plainTravelerCell(row, hasRecord)
    }

    const plainTravelerCell = (row: UnifiedCodeRow, hasRecord: boolean) => (
        <Td>
            {row.traveler?.name ? (
                row.codeId ? (
                    <button type="button" disabled={!hasRecord} onClick={(e) => {e.stopPropagation(); openEdit(row.codeId!, 'traveler')}}
                            className="group flex items-center gap-2 text-left disabled:cursor-not-allowed">
                        <TravelerCell traveler={row.traveler}/>
                        <IconEdit className="size-3 text-neutralBlack-300 group-hover:text-neutralBlack-600"/>
                    </button>
                ) : <TravelerCell traveler={row.traveler}/>
            ) : row.codeId ? (
                <button type="button" disabled={!hasRecord} onClick={(e) => {e.stopPropagation(); openEdit(row.codeId!, 'traveler')}} className="disabled:cursor-not-allowed">
                    <AddPill label={t('packs.addTraveler')}/>
                </button>
            ) : <Dash/>}
        </Td>
    )

    const tripCell = (row: UnifiedCodeRow, hasRecord: boolean) => (
        <Td>
            {row.trip ? (
                row.codeId ? (
                    <button type="button" disabled={!hasRecord} onClick={(e) => {e.stopPropagation(); openEdit(row.codeId!, 'trip')}}
                            className="group flex items-center gap-2 text-left disabled:cursor-not-allowed">
                        <TripCell trip={row.trip}/>
                        <IconEdit className="size-3 text-neutralBlack-300 group-hover:text-neutralBlack-600"/>
                    </button>
                ) : <TripCell trip={row.trip}/>
            ) : row.codeId ? (
                <button type="button" disabled={!hasRecord} onClick={(e) => {e.stopPropagation(); openEdit(row.codeId!, 'trip')}} className="disabled:cursor-not-allowed">
                    <AddPill label={t('addTrip')}/>
                </button>
            ) : <Dash/>}
        </Td>
    )

    const tripbookCell = (row: UnifiedCodeRow) => (
        <Td>
            {row.flipbookUrl ? (
                <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <a href={row.flipbookUrl} target="_blank" rel="noreferrer" className="text-[13px] whitespace-nowrap text-[#3E63DD] hover:underline">{t('packs.viewTripbook')}</a>
                    <CopyButton active={copied === `tripbook-${row.key}`} onClick={() => copyLink(`tripbook-${row.key}`, row.flipbookUrl!)}/>
                </span>
            ) : <Dash/>}
        </Td>
    )

    const feedbackCell = (row: UnifiedCodeRow) => (
        <Td>
            <span className="inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                <span className="relative inline-block">
                    {row.feedback ? (
                        <button type="button" title={t('packs.viewTravelerFeedback')}
                                onClick={() => setFeedbackPopoverKey((k) => (k === row.key ? null : row.key))}
                                className="w-7 h-7 flex items-center justify-center rounded-full text-neutralBlack-700 hover:bg-neutralBlack-100">
                            <FeedbackIcon className="size-3.5"/>
                        </button>
                    ) : row.tripbookId && isAdmin ? (
                        <button type="button" title={t('packs.addClientFeedback')} onClick={() => openFeedbackEditor(row)}
                                className="w-6 h-6 flex items-center justify-center rounded-full border border-dashed border-neutralBlack-300 text-neutralBlack-400 hover:text-neutralBlack-900 hover:border-neutralBlack-500">
                            <IconPlus className="size-2.5"/>
                        </button>
                    ) : <Dash/>}
                    {feedbackPopoverKey === row.key && row.feedback && (
                        <FeedbackPopover
                            row={row}
                            canEdit={isAdmin}
                            onEdit={() => openFeedbackEditor(row)}
                            onClose={() => setFeedbackPopoverKey(null)}
                        />
                    )}
                </span>
                {canCancelRow(row) && cancelButton(row)}
                {canHideRow(row) && hideButton(row)}
            </span>
        </Td>
    )

    const deliveryCell = (row: UnifiedCodeRow) => (
        <Td>
            {row.delivery ? (
                <span className="flex flex-col gap-1.5 items-start">
                    <DeliveryPill status={row.delivery.status}/>
                    {row.delivery.trackingUrl && row.delivery.status !== "DELIVERED" && (
                        <a href={row.delivery.trackingUrl} target="_blank" rel="noreferrer"
                           onClick={(e) => e.stopPropagation()}
                           className="inline-flex items-center gap-1.5 text-[12px] whitespace-nowrap text-[#3E63DD] hover:underline">
                            <IconPackage className="size-5 flex-none"/>
                            {t('packs.sidePanelFollowOrder')}
                        </a>
                    )}
                </span>
            ) : <Dash/>}
        </Td>
    )

    // Row shape differs per table, matching the design's three distinct
    // column sets instead of one flat table with columns hidden per bucket.
    const renderPrintedRow = (row: UnifiedCodeRow) => (
        <tr key={row.key} onClick={() => setOpenRowKey(row.key)}
            className="cursor-pointer border-b border-neutralBlack-100 last:border-0 hover:bg-neutralBlack-50">
            {statusCell(row, !!row.codeId && invitationCodesById.has(row.codeId))}
            {codeCell(row)}
            {travelerCell(row, !!row.codeId && invitationCodesById.has(row.codeId))}
            {tripCell(row, !!row.codeId && invitationCodesById.has(row.codeId))}
            {tripbookCell(row)}
            {deliveryCell(row)}
            {feedbackCell(row)}
        </tr>
    )

    const renderHiddenRow = (row: UnifiedCodeRow) => {
        const hidden = row.tripId ? hiddenByTripId.get(row.tripId) : undefined
        return (
            <tr key={row.key} className="border-b border-neutralBlack-100 last:border-0 text-neutralBlack-500">
                {codeCell(row)}
                <Td>{row.traveler ? <TravelerCell traveler={row.traveler}/> : <Dash/>}</Td>
                <Td>{row.trip ? <TripCell trip={row.trip}/> : <Dash/>}</Td>
                <Td><span className="text-[13px]">{hidden?.reason || <Dash/>}</span></Td>
                <Td>
                    <span className="flex flex-col text-[12.5px]">
                        <span>{hidden?.hiddenByName || hidden?.hiddenByEmail || <Dash/>}</span>
                        {hidden && <span className="text-neutralBlack-400">{formatDate(hidden.hiddenAt.slice(0, 10))}</span>}
                    </span>
                </Td>
                <Td align="right">
                    <button type="button" disabled={isChangingHidden || !row.tripId}
                            onClick={() => unhideUnit(row.tripId!).catch(userReadableDescription)}
                            className="inline-flex items-center h-[26px] px-2.5 rounded-full border border-neutralBlack-300 bg-white text-xs font-medium text-neutralBlack-900 hover:bg-neutralBlack-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                        {t('packs.unhideTrip')}
                    </button>
                </Td>
            </tr>
        )
    }

    // Members of an expanded group sit on a tinted background and leave the
    // trip to the group row above them.
    const rowClass = (isMember: boolean) =>
        `cursor-pointer border-b border-neutralBlack-100 last:border-0 ${isMember ? 'bg-neutralBlack-50 hover:bg-[#F3F3F5]' : 'hover:bg-neutralBlack-50'}`

    const renderCreatedRow = (row: UnifiedCodeRow, isMember = false) => {
        const hasRecord = !!row.codeId && invitationCodesById.has(row.codeId)
        return (
            <tr key={row.key} onClick={() => setOpenRowKey(row.key)} className={rowClass(isMember)}>
                {codeCell(row, isMember)}
                <PdfCell code={row.code}/>
                {deliverableCell(row)}
                {statusCell(row, hasRecord)}
                {travelerCell(row, hasRecord)}
                {isMember ? <Td/> : tripCell(row, hasRecord)}
                {feedbackCell(row)}
            </tr>
        )
    }

    const renderUnassignedRow = (row: UnifiedCodeRow, isMember = false) => {
        const hasRecord = !!row.codeId && invitationCodesById.has(row.codeId)
        return (
            <tr key={row.key} onClick={() => setOpenRowKey(row.key)} className={rowClass(isMember)}>
                {codeCell(row, isMember)}
                <PdfCell code={row.code}/>
                {deliverableCell(row)}
                {unassignedStatusCell(row, hasRecord)}
                {travelerCell(row, hasRecord)}
                {isMember ? <Td/> : tripCell(row, hasRecord)}
                <Td align="right">{canCancelRow(row) && cancelButton(row)}</Td>
            </tr>
        )
    }

    // Both tables share a column order (code, PDF, deliverable, status, traveler,
    // trip, actions), so one group row fits either: toggle + count, the group
    // pill under "Estado", the shared trip under "Viaje".
    const itemRenderer = (renderRow: (row: UnifiedCodeRow, isMember?: boolean) => React.ReactNode) => (item: CodeTableItem) => {
        if (item.kind === 'row') return renderRow(item.row)
        const isOpen = expandedGroups.isExpanded(item.key)
        const trip = item.rows.find((r) => r.trip)?.trip ?? null
        return (
            <React.Fragment key={item.key}>
                <tr onClick={() => expandedGroups.toggle(item.key)} aria-expanded={isOpen}
                    className="cursor-pointer border-b border-neutralBlack-100 last:border-0 bg-white hover:bg-neutralBlack-50">
                    <Td><GroupToggle isOpen={isOpen} count={item.rows.length}/></Td>
                    <Td/>
                    <Td/>
                    <Td><GroupPill kind="individual"/></Td>
                    <Td/>
                    <Td>{trip ? <TripCell trip={trip}/> : <Dash/>}</Td>
                    <Td/>
                </tr>
                {isOpen && item.rows.map((row) => renderRow(row, true))}
            </React.Fragment>
        )
    }

    return (
        <>
            <div className="flex flex-col gap-3">
                <CodeTableSection
                    dotColor="bg-success"
                    title={t('packs.printedSectionTitle')}
                    countLabel={totalUnits != null
                        ? t('packs.printedSectionCount', {n: formatNumber(printedBooks), total: formatNumber(totalUnits)})
                        : t('packs.printedSectionCountSimple', {n: formatNumber(printedBooks)})}
                    emptyLabel={t('packs.printedSectionEmpty')}
                    columns={[t('packs.columnStatus'), t('packs.columnCode'), t('packs.columnTraveler'), t('packs.columnTrip'), t('packs.columnTripbook'), t('packs.columnDelivery'), t('packs.columnFeedback')]}
                    paging={printedPaging}
                    totalItems={printedRows.length}
                    renderRow={renderPrintedRow}
                    defaultOpen
                />

                <CodeTableSection
                    dotColor="bg-warning"
                    title={t('packs.createdSectionTitle')}
                    countLabel={t('packs.createdSectionCount', {n: formatNumber(createdRows.length)})}
                    emptyLabel={t('packs.createdSectionEmpty')}
                    columns={[t('packs.columnCode'), t('packs.columnPdf'), t('packs.columnDeliverable'), t('packs.columnStatus'), t('packs.columnTraveler'), t('packs.columnTrip'), t('packs.columnFeedback')]}
                    paging={createdPaging}
                    totalItems={createdItems.length}
                    renderRow={itemRenderer(renderCreatedRow)}
                />

                <CodeTableSection
                    dotColor="bg-neutralBlack-400"
                    title={t('packs.unassignedSectionTitle')}
                    emptyLabel={t('packs.unassignedSectionEmpty')}
                    columns={[t('packs.columnCode'), t('packs.columnPdf'), t('packs.columnDeliverable'), t('packs.columnStatus'), t('packs.columnTraveler'), t('packs.columnTrip'), '']}
                    paging={unassignedPaging}
                    totalItems={unassignedItems.length}
                    renderRow={itemRenderer(renderUnassignedRow)}
                    help={t('dialogs.prefillHelp')}
                    headerRight={
                        <button
                            type="button"
                            onClick={openAssignDialog}
                            className="flex-none inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#0F7A45]/[.24] bg-[#DFF3E7] text-[13.5px] font-semibold text-[#0D6B3C] shadow-[0_1px_2px_rgba(15,122,69,.10)] hover:bg-[#CFEBDA] hover:border-[#0F7A45] whitespace-nowrap"
                        >
                            <IconPlus className="size-3"/>
                            {t('packs.prefillCode')}
                        </button>
                    }

                />

                {isAdmin && hiddenRows.length > 0 && (
                    <CodeTableSection
                        dotColor="bg-neutralBlack-300"
                        title={t('packs.hiddenSectionTitle')}
                        countLabel={t('packs.hiddenSectionCount', {n: formatNumber(hiddenRows.length)})}
                        emptyLabel=""
                        help={t('packs.hiddenSectionHelp')}
                        columns={[t('packs.columnCode'), t('packs.columnTraveler'), t('packs.columnTrip'), t('packs.columnHiddenReason'), t('packs.columnHiddenBy'), '']}
                        paging={hiddenPaging}
                        totalItems={hiddenRows.length}
                        renderRow={renderHiddenRow}
                        defaultOpen={false}
                    />
                )}
            </div>

            <AssignInvitationCodeDialog
                ref={assignDialog}
                agencyId={agencyId}
                availableCodes={availableCodes}
                pageOptions={pageOptions}
                defaultPages={agencyPages}
                primaryMarket={primaryMarket}
                onAssigned={onCodeCreated}
            />
            <EditInvitationCodeDialog ref={editDialog} mode={editingMode} record={editingRecord} isSaving={isUpdatingInvitationCode} onSave={handleSaveEdit}/>
            <EditTripbookFeedbackDialog
                ref={feedbackDialog}
                code={feedbackRow?.code ?? null}
                feedback={feedbackRow?.feedback ?? null}
                isSaving={isSettingTripbookFeedback}
                isDeleting={isDeletingTripbookFeedback}
                onSave={handleSaveFeedback}
                onDelete={handleDeleteFeedback}
            />
            <SharedAlbumTravelersDialog
                ref={travelersDialog}
                record={travelersRecord}
                isSaving={isUpdatingInvitationCode}
                onSave={handleSaveTravelers}
            />
            <ConfirmDialog
                ref={cancelDialog}
                onConfirm={confirmCancel}
                title={t('packs.cancelCodeConfirmTitle')}
                message={t('packs.cancelCodeConfirmMessage')}
                confirmLabel={t('packs.cancelCode')}
                cancelLabel={t('cancel')}
            />
            <ConfirmDialog
                ref={hideDialog}
                onConfirm={confirmHide}
                title={t('packs.hideTripConfirmTitle')}
                confirmLabel={t('packs.hideTrip')}
                cancelLabel={t('cancel')}
            >
                <div className="mb-8 flex flex-col gap-3">
                    <p>{t('packs.hideTripConfirmMessage')}</p>
                    <label className="flex flex-col gap-1.5 text-[13px] text-neutralBlack-700">
                        {t('packs.hideTripReason')}
                        <input type="text" value={hideReason} maxLength={200}
                               onChange={(e) => setHideReason(e.target.value)}
                               placeholder={t('packs.hideTripReasonPlaceholder')}
                               className="h-10 px-3 rounded-lg border border-neutralBlack-300 text-sm text-neutralBlack-900 focus:outline-none focus:border-neutralBlack-700"/>
                    </label>
                </div>
            </ConfirmDialog>

            {openRow && (
                <TripbookSidePanel
                    row={openRow}
                    packContext={packContext ?? ''}
                    canEditCode={!!openRow.codeId && invitationCodesById.has(openRow.codeId)}
                    canEditFeedback={isAdmin}
                    isDelivered={openRow.lifecycle === 'DELIVERED'}
                    itineraryUrl={openRow.codeId ? invitationCodesById.get(openRow.codeId)?.itinerary?.tripItineraryPdfUrl : undefined}
                    onClose={() => setOpenRowKey(null)}
                    onEditTraveler={() => openRow.codeId && openEdit(openRow.codeId, 'traveler')}
                    onEditTrip={() => openRow.codeId && openEdit(openRow.codeId, 'trip')}
                    onEditFeedback={() => openFeedbackEditor(openRow)}
                    onStatusChange={LIFECYCLE_EDITABLE.includes(openRow.lifecycle) ? (delivered) => handleStatusChange(openRow, delivered) : undefined}
                />
            )}
        </>
    )
}

export type Paging<T = UnifiedCodeRow> = ReturnType<typeof usePagedSlice<T>>

// A column is either a plain header label (auto width, left aligned) or the
// design's fixed-width spec — the code tables lay out with `table-layout:fixed`
// so "Estado" keeps its 290px lane no matter how long a pill grows.
export type CodeTableColumn = {label: string; width?: number; align?: 'left' | 'right' | 'center'}

const ALIGN_CLASS = {left: 'text-left', right: 'text-right', center: 'text-center'} as const

// Horizontal padding lives on the table, not on Td: the design gutters the
// first/last cell against the card edge (22px) and keeps the inner columns
// tighter (14px), which a per-cell class can't express.
const CELL_PADDING =
    '[&_th]:px-3.5 [&_td]:px-3.5 [&_tr>*:first-child]:pl-[22px] [&_tr>*:first-child]:pr-[22px] [&_tr>*:last-child]:pr-[22px]'

// Generic over the row item so a table can page over something other than one
// code per row — the agency's "Códigos asignados" pages over group items (see
// AgencySelfServiceCodeSections), each rendering a parent row plus its members.
export function CodeTableSection<T = UnifiedCodeRow>({
    dotColor, icon, title, count, countLabel, help, emptyLabel, columns, minWidth = 900,
    defaultOpen = true, paging, totalItems, renderRow, headerRight, tip,
}: {
    dotColor?: string
    // Replaces the status dot — "Pendientes de entrega" leads with the pending
    // clock instead, since it's a waiting state rather than a lifecycle stage.
    icon?: React.ReactNode
    title: string
    // Rounded badge with the bucket size, per the design. `countLabel` is the
    // older inline sentence, still used by the internal (51trips staff) tables.
    count?: number
    countLabel?: string
    help?: React.ReactNode
    emptyLabel: string
    columns: (string | CodeTableColumn)[]
    minWidth?: number
    defaultOpen?: boolean
    paging: Paging<T>
    totalItems: number
    renderRow: (row: T) => React.ReactNode
    // "Códigos prerellenados" is the only section with a header action (the
    // "Prerellenar un código" button) and a conversion-tip banner.
    headerRight?: React.ReactNode
    tip?: React.ReactNode
}) {
    const [isOpen, setIsOpen] = useState(defaultOpen)
    const cols: CodeTableColumn[] = columns.map((c) => (typeof c === 'string' ? {label: c} : c))
    const hasWidths = cols.some((c) => c.width != null)
    return (
    <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-[#FCFCFD] flex items-center gap-2.5 px-[22px] pt-4 pb-3 flex-wrap">
            <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((o) => !o)}
                className="flex-1 min-w-[220px] flex items-center gap-2.5 flex-wrap text-left"
            >
                <span className="inline-flex items-center gap-2">
                    {icon ?? <span className={`w-[7px] h-[7px] flex-none rounded-full ${dotColor ?? 'bg-neutralBlack-400'}`}/>}
                    <span className="text-[14px] font-semibold tracking-tight text-neutralBlack-900">{title}</span>
                    {count != null && (
                        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-[7px] rounded-full bg-neutralBlack-100 text-xs font-semibold text-neutralBlack-700">
                            {formatNumber(count)}
                        </span>
                    )}
                </span>
                {help && <span className="flex-1 min-w-[150px] text-[13px] leading-relaxed text-neutralBlack-500">{help}</span>}
                {countLabel && <span className="text-[13px] text-neutralBlack-500">{countLabel}</span>}
            </button>
            {headerRight}
            <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((o) => !o)}
                className="ml-auto flex-none w-[26px] h-[26px] flex items-center justify-center rounded-full bg-neutralBlack-100 text-neutralBlack-700 hover:bg-neutralBlack-200"
            >
                <IconChevronDown className={`w-[11px] h-[11px] ${isOpen ? '' : '-rotate-90'}`}/>
            </button>
        </div>

        {isOpen && (
            <>
                {tip && (
                    <div className="mx-[22px] mb-3.5 flex items-center gap-3 bg-[#FDF7E6] border border-[#B07A002E] rounded-xl px-4 py-3">
                        <IdeaIcon className="size-5 flex-none text-[#7A5C0B]"/>
                        <span className="text-[13px] text-[#7A5C0B] leading-relaxed text-neutralBlack-700">{tip}</span>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table
                        className={`w-full border-collapse text-[13.5px] ${hasWidths ? 'table-fixed' : ''} ${CELL_PADDING}`}
                        style={{minWidth: `${minWidth}px`}}
                    >
                        {hasWidths && (
                            <colgroup>
                                {cols.map((c, i) => <col key={i} style={c.width != null ? {width: `${c.width}px`} : undefined}/>)}
                            </colgroup>
                        )}
                        <thead>
                            <tr>
                                {cols.map((c, i) => (
                                    <th key={i} className={`${ALIGN_CLASS[c.align ?? 'left']} py-2.5 text-[11px] font-medium uppercase tracking-wide text-neutralBlack-500 border-y border-neutralBlack-200`}>
                                        {c.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {paging.pageItems.length === 0 ? (
                                <EmptyRow colSpan={cols.length} label={emptyLabel}/>
                            ) : paging.pageItems.map(renderRow)}
                        </tbody>
                    </table>
                </div>

                {paging.pageCount > 1 && <PaginationFooter {...paging} totalItems={totalItems}/>}
            </>
        )}
    </section>
    )
}

export const DeliveryPill: React.FC<{status: keyof typeof DELIVERY_LABEL_KEY}> = ({status}) => {
    const {t} = useTranslation('dashboard')
    const style = DELIVERY_STYLE[status] ?? DELIVERY_STYLE.IN_PRODUCTION
    return (
        <span className={`inline-flex items-center rounded-full border px-[11px] py-1 text-xs font-medium whitespace-nowrap ${style}`}>
            {t(DELIVERY_LABEL_KEY[status])}
        </span>
    )
}

export const FeedbackIcon: React.FC<{className?: string}> = ({className}) => (
    <svg viewBox="0 0 20 20" fill="none" className={className}>
        <path d="M3.5 4.5h13a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H8.2l-3.4 2.6V13.5h-1.3a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
              stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    </svg>
)

export const FeedbackPopover: React.FC<{row: UnifiedCodeRow; canEdit: boolean; onEdit: () => void; onClose: () => void}> = ({row, canEdit, onEdit, onClose}) => {
    const {t} = useTranslation('dashboard')
    const ref = useRef<HTMLDivElement>(null)
    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose()
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [onClose])

    const name = row.traveler?.name
    const initials = name ? name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() : '—'

    return (
        <div ref={ref} className="absolute z-30 top-full right-0 mt-2 w-[300px] bg-white border border-neutralBlack-200 rounded-2xl shadow-xl p-4 flex flex-col gap-3 text-left normal-case">
            <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 flex-none rounded-full bg-neutralBlack-100 flex items-center justify-center text-[11.5px] font-semibold text-neutralBlack-700">{initials}</span>
                <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-neutralBlack-900">{name ?? t('packs.travelerNoName')}</span>
                    <span className="text-[11.5px] text-neutralBlack-400">{row.code}</span>
                </span>
            </div>
            <span className="text-[13.5px] leading-relaxed text-neutralBlack-900">{row.feedback?.comment}</span>
            <span className="flex items-center gap-2.5">
                <span className="flex-1 min-w-0 text-[11.5px] text-neutralBlack-400">
                    {row.feedback?.channel} · {formatDate(row.feedback?.feedbackDate ?? null)}
                </span>
                {canEdit && (
                    <button type="button" onClick={onEdit} className="inline-flex items-center gap-1 text-xs text-[#3E63DD] hover:underline">
                        <IconEdit className="size-2.5"/>
                        {t('packs.edit')}
                    </button>
                )}
            </span>
        </div>
    )
}

const PaginationFooter: React.FC<{
    page: number
    pageCount: number
    setPage: (updater: (p: number) => number) => void
    totalItems: number
}> = ({page, pageCount, setPage, totalItems}) => {
    const {t} = useTranslation('dashboard')
    if (pageCount <= 1) return null
    return (
        <div className="flex items-center gap-3 flex-wrap px-[22px] py-3">
            <span className="text-[12.5px] text-neutralBlack-500">
                {t('packs.paginationOf', {
                    from: formatNumber(page * PAGE_SIZE + 1),
                    to: formatNumber(Math.min(totalItems, (page + 1) * PAGE_SIZE)),
                    total: formatNumber(totalItems),
                })}
            </span>
            <span className="flex-1 min-w-[12px]"/>
            <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="w-7 h-7 flex items-center justify-center rounded-full border border-neutralBlack-200 bg-white text-neutralBlack-800 disabled:text-neutralBlack-300 disabled:cursor-not-allowed hover:bg-neutralBlack-50 disabled:hover:bg-white"
            >
                <IconChevronDown className="w-2.5 h-2.5 rotate-90"/>
            </button>
            <button
                type="button"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                className="w-7 h-7 flex items-center justify-center rounded-full border border-neutralBlack-200 bg-white text-neutralBlack-800 disabled:text-neutralBlack-300 disabled:cursor-not-allowed hover:bg-neutralBlack-50 disabled:hover:bg-white"
            >
                <IconChevronDown className="w-2.5 h-2.5 -rotate-90"/>
            </button>
        </div>
    )
}

// Horizontal padding is applied by CodeTableSection's table (CELL_PADDING) so
// the first/last column can hug the card gutter — Td only owns the vertical rhythm.
// `clip` keeps long content inside its fixed lane; it's opt-in because cells
// that host a popover or menu (feedback, status) must be free to overflow.
export const Td: React.FC<{children?: React.ReactNode; align?: 'left' | 'right' | 'center'; clip?: boolean}> = ({children, align = 'left', clip}) => (
    <td className={`py-3 align-middle whitespace-nowrap ${clip ? 'overflow-hidden' : ''} ${ALIGN_CLASS[align]}`}>{children}</td>
)

export const Dash: React.FC = () => <span className="text-neutralBlack-400">—</span>

export const AddPill: React.FC<{label: string}> = ({label}) => (
    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-neutralBlack-500 border border-dashed border-neutralBlack-300 rounded-full px-2.5 py-1 hover:text-neutralBlack-900 hover:border-neutralBlack-500">
        <IconPlus className="size-2.5"/>
        {label}
    </span>
)

export const EmptyRow: React.FC<{colSpan: number; label: string}> = ({colSpan, label}) => (
    <tr>
        <td colSpan={colSpan} className="py-[26px] text-center text-[13.5px] text-neutralBlack-500">{label}</td>
    </tr>
)

// "Entregable · PDF" column — a PDF badge plus an explicit download button,
// both triggering the same downloadEbookPdf request: there's no separate
// public URL to preview the ebook, so the badge can't be a plain <a href>.
// Shown for pendientes de entrega / entregados / TripBooks creados — not for
// impresos, which already surfaces its TripBook via tripbookCell/deliveryCell.
export const PdfCell: React.FC<{code: string | null}> = ({code}) => {
    const {t} = useTranslation('dashboard')
    return (
        <Td clip>
            {code ? (
                <span className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => downloadEbookPdf(code)} title={t('packs.downloadPdf')}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#8C2F39]/[.14] bg-[#F7E8E9] pl-2 pr-2.5 py-1 hover:bg-[#F0DADC]">
                        <IconPdfDoc className="w-[13px] h-[15px] flex-none text-[#8C2F39]"/>
                        <span className="text-[12.5px] font-semibold text-[#8C2F39]">PDF</span>
                    </button>
                    <button type="button" title={t('packs.downloadPdf')} onClick={() => downloadEbookPdf(code)}
                            className="w-[22px] h-[22px] flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-neutralBlack-100 hover:text-neutralBlack-900">
                        <IconDownload className="size-3"/>
                    </button>
                </span>
            ) : <Dash/>}
        </Td>
    )
}

export const CopyButton: React.FC<{active: boolean; onClick: () => void}> = ({active, onClick}) => {
    const {t} = useTranslation('dashboard')
    return (
        <button type="button" title={t('packs.copyLink')} onClick={onClick}
                className="w-[22px] h-[22px] inline-flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-neutralBlack-100 hover:text-neutralBlack-900">
            {active ? <span className="text-[10px] text-success">✓</span> : <IconCopy className="size-3"/>}
        </button>
    )
}

export const TravelerCell: React.FC<{traveler: PackTraveler}> = ({traveler}) => (
    traveler.name || traveler.email || traveler.phone ? (
        <span className="flex flex-col gap-0.5">
            <span className="text-neutralBlack-900">{traveler.name ?? <Dash/>}</span>
            <span className="text-xs text-neutralBlack-500">{traveler.email ?? traveler.phone ?? ''}</span>
        </span>
    ) : <Dash/>
)

export const TripCell: React.FC<{trip: PackTrip}> = ({trip}) => (
    <span className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1.5 text-neutralBlack-700">
            {trip.countries.map((c) => (
                <img key={c.iso2} src={getFlagUrlFromIso2(c.iso2)} alt={c.name}
                     className="w-3.5 h-2.5 rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>
            ))}
            {trip.countries.map((c) => c.name).join(' · ') || <Dash/>}
        </span>
        {(trip.startDate || trip.endDate) && (
            <span className="text-xs text-neutralBlack-500">
                {formatDate(trip.startDate)} – {formatDate(trip.endDate)}
            </span>
        )}
    </span>
)
